import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { replaceInquirySelection } from '@/services/api/chatService';
import { getFolderDescendantsBatch } from '@/services/api/folderService';
import {
  applyFileSelection,
  applyFolderSelection,
  chunkIds,
  computePersistedFolderIds,
  DescendantsCache,
  FolderSelectionState,
  getFolderSelectionState,
  isSameSnapshot,
  mergeSelections,
  SelectionSnapshot,
  toIdList,
} from './selectionModel';

/** Selection changes are coalesced for this long before being persisted. */
export const PERSIST_DEBOUNCE_MS = 400;

/** Delay before the single automatic retry of a failed write. */
export const PERSIST_RETRY_DELAY_MS = 800;

/** Maximum folders resolved per descendants request. */
export const DESCENDANTS_BATCH_SIZE = 100;

/**
 * Upper bound on writes triggered by one drain, so repeated conflicts cannot
 * turn into an endless request loop.
 */
const MAX_WRITES_PER_DRAIN = 6;

export type SelectionStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseInquirySelectionOptions {
  inquiryId: string;
  inquiry: any;
  /** Reports a persistence failure so the surface can show it. */
  onPersistError?: (message?: string) => void;
  /** Reports that the selection was replaced by a newer one from elsewhere. */
  onExternalChange?: () => void;
}

export interface InquirySelectionApi {
  selectedFileIds: ReadonlySet<string>;
  selectedFileCount: number;
  status: SelectionStatus;
  isFileSelected: (fileId: string) => boolean;
  getFolderState: (folderId: string) => FolderSelectionState;
  /**
   * False until the folder's descendants are known. Its reported state is not
   * trustworthy before that.
   */
  isFolderResolved: (folderId: string) => boolean;
  setFileSelected: (fileId: string, shouldSelect: boolean) => void;
  setFolderSelected: (folderId: string, shouldSelect: boolean) => Promise<void>;
  setRecordsSelected: (records: any[], shouldSelect: boolean) => Promise<void>;
  ensureFolderStates: (folderIds: string[]) => Promise<void>;
  /** Persist any pending change immediately and wait for it to settle. */
  flush: () => Promise<void>;
  retry: () => Promise<void>;
}

const numericIds = (ids: readonly string[]): string[] =>
  ids.filter(id => /^\d+$/.test(id));

/**
 * Owns the selection of one inquiry: optimistic local state, derived folder
 * states, and automatic persistence.
 *
 * Writes are debounced and single-flight, and every write carries the version
 * it was based on, so rapid changes and slow responses cannot resurrect an
 * older selection. A failed write is retried once and then reverted to the
 * last state the backend confirmed, so the UI never shows a selection the
 * backend did not accept.
 */
export const useInquirySelection = ({
  inquiryId,
  inquiry,
  onPersistError,
  onExternalChange,
}: UseInquirySelectionOptions): InquirySelectionApi => {
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [status, setStatus] = useState<SelectionStatus>('idle');
  // Mutating a Map does not re-render, so views are invalidated explicitly.
  const [descendantsRevision, setDescendantsRevision] = useState(0);

  const descendantsRef = useRef<DescendantsCache>(new Map());
  const pendingFoldersRef = useRef<Map<string, Promise<void>>>(new Map());

  const selectedRef = useRef<Set<string>>(selectedFileIds);
  const versionRef = useRef(0);
  const persistedFolderIdsRef = useRef<string[]>([]);
  const lastPersistedRef = useRef<{
    fileIds: Set<string>;
    folderIds: string[];
  }>({ fileIds: new Set(), folderIds: [] });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drainRef = useRef<Promise<boolean> | null>(null);
  const dirtyRef = useRef(false);
  const hydratedInquiryRef = useRef<string | null>(null);
  const unmountedRef = useRef(false);
  // Lets the unmount cleanup start a write without re-running on every change.
  const latestDrainRef = useRef<() => Promise<boolean>>(async () => true);

  const callbacksRef = useRef({ onPersistError, onExternalChange });
  callbacksRef.current = { onPersistError, onExternalChange };

  const applySelection = useCallback((next: Set<string>) => {
    selectedRef.current = next;
    if (!unmountedRef.current) {
      setSelectedFileIds(next);
    }
  }, []);

  useEffect(() => {
    unmountedRef.current = false;

    return () => {
      unmountedRef.current = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      // Leaving the surface must not discard a change that was still waiting
      // out the debounce window.
      if (dirtyRef.current) {
        void latestDrainRef.current();
      }
    };
  }, []);

  const buildSnapshot = useCallback((): SelectionSnapshot => {
    const fileIds = numericIds([...selectedRef.current]);
    const folderIds = numericIds(
      computePersistedFolderIds(
        selectedRef.current,
        descendantsRef.current,
        persistedFolderIdsRef.current,
      ),
    );

    return { fileIds, folderIds };
  }, []);

  const writeOnce = useCallback(
    async (snapshot: SelectionSnapshot, attempt: number): Promise<boolean> => {
      const response = await replaceInquirySelection(inquiryId, {
        base_version: versionRef.current,
        folder_ids: snapshot.folderIds,
        file_ids: snapshot.fileIds,
      });

      if (!response?.success || !response.data) {
        if (attempt === 0) {
          await new Promise(resolve =>
            setTimeout(resolve, PERSIST_RETRY_DELAY_MS),
          );

          return writeOnce(snapshot, attempt + 1);
        }

        // The change stays on screen so the user does not lose it, but it is
        // reported as unsaved and stays queued for an explicit retry.
        callbacksRef.current.onPersistError?.(response?.error);

        return false;
      }

      const result = response.data;
      const serverVersion = Number(result.selection_version ?? 0);
      const serverFileIds = toIdList(result.file_ids);
      const serverFolderIds = toIdList(result.folder_ids);

      versionRef.current = serverVersion;
      persistedFolderIdsRef.current = serverFolderIds;

      if (result.status === 'conflict') {
        // Someone else advanced the selection. Replay the local changes onto
        // the newer selection and let the drain loop write the result.
        const merged = mergeSelections(
          serverFileIds,
          lastPersistedRef.current.fileIds,
          selectedRef.current,
        );

        lastPersistedRef.current = {
          fileIds: new Set(serverFileIds),
          folderIds: serverFolderIds,
        };

        applySelection(merged);
        dirtyRef.current = true;
        callbacksRef.current.onExternalChange?.();

        return true;
      }

      const reconciled = mergeSelections(
        serverFileIds,
        new Set(snapshot.fileIds),
        selectedRef.current,
      );

      lastPersistedRef.current = {
        fileIds: new Set(serverFileIds),
        folderIds: serverFolderIds,
      };
      applySelection(reconciled);

      return true;
    },
    [inquiryId, applySelection],
  );

  /** Resolves to false when the selection could not be fully persisted. */
  const drain = useCallback((): Promise<boolean> => {
    if (drainRef.current) {
      // A write is in flight; it re-checks for newer changes before finishing.
      return drainRef.current;
    }

    const run = (async () => {
      let writes = 0;

      while (dirtyRef.current && writes < MAX_WRITES_PER_DRAIN) {
        const snapshot = buildSnapshot();

        if (
          isSameSnapshot(snapshot, {
            fileIds: [...lastPersistedRef.current.fileIds],
            folderIds: lastPersistedRef.current.folderIds,
          })
        ) {
          dirtyRef.current = false;
          break;
        }

        dirtyRef.current = false;

        if (!unmountedRef.current) {
          setStatus('saving');
        }

        writes += 1;

        if (!(await writeOnce(snapshot, 0))) {
          // Stays queued so an explicit retry, a later change or closing the
          // surface writes it again.
          dirtyRef.current = true;
          break;
        }
      }

      // Anything still queued here failed or exceeded the write budget, and
      // must not be reported as saved.
      const settled = !dirtyRef.current;

      if (!unmountedRef.current) {
        setStatus(settled ? 'saved' : 'error');
      }

      return settled;
    })().finally(() => {
      drainRef.current = null;
    });

    drainRef.current = run;

    return run;
  }, [buildSnapshot, writeOnce]);

  latestDrainRef.current = drain;

  const schedulePersist = useCallback(() => {
    dirtyRef.current = true;

    // The change is not stored yet, so the surface must stop claiming it is
    // while the debounce window runs.
    if (!unmountedRef.current) {
      setStatus('saving');
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void drain();
    }, PERSIST_DEBOUNCE_MS);
  }, [drain]);

  /**
   * Adopt the stored selection when the inquiry changes, or when the backend
   * reports a version newer than the one this surface is working from.
   * Local pending edits are never overwritten by an equal or older version.
   */
  useEffect(() => {
    if (!inquiry || !inquiryId) {
      return;
    }

    // During a chat switch the loaded inquiry can lag behind the requested ID.
    // Hydrating from it would apply another chat's selection to this one.
    if (inquiry.id !== undefined && String(inquiry.id) !== String(inquiryId)) {
      return;
    }

    const serverVersion = Number(inquiry.selection_version ?? 0);
    const isNewInquiry = hydratedInquiryRef.current !== inquiryId;

    if (!isNewInquiry && serverVersion <= versionRef.current) {
      return;
    }

    // Only the browsable files belong to this picker: chat uploads are stored
    // alongside them but are managed by the attachment endpoints.
    const fileIds = toIdList(inquiry.selection_file_ids ?? inquiry.files);
    const folderIds = toIdList(inquiry.folders);
    const previouslyPersisted = lastPersistedRef.current.fileIds;
    // A change of this surface that has not reached the backend yet. A write in
    // flight counts too: its result is not confirmed, and `dirtyRef` was
    // already cleared when it started.
    const hasPendingEdit =
      !isNewInquiry && (dirtyRef.current || drainRef.current !== null);

    hydratedInquiryRef.current = inquiryId;
    versionRef.current = serverVersion;
    persistedFolderIdsRef.current = folderIds;
    lastPersistedRef.current = { fileIds: new Set(fileIds), folderIds };

    if (isNewInquiry) {
      descendantsRef.current = new Map();
      pendingFoldersRef.current = new Map();
      setDescendantsRevision(revision => revision + 1);
    }

    if (!hasPendingEdit) {
      dirtyRef.current = false;
      applySelection(new Set(fileIds));
      setStatus('idle');

      return;
    }

    // The stored selection moved on elsewhere. Replay the pending edit on top
    // of it instead of dropping either side, then persist the result.
    applySelection(
      mergeSelections(fileIds, previouslyPersisted, selectedRef.current),
    );
    schedulePersist();
  }, [inquiry, inquiryId, applySelection, schedulePersist]);

  const flush = useCallback(async (): Promise<void> => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const settled = await drain();

    // A change made while the last write was in flight still needs writing.
    // After a failure the change stays queued for an explicit retry instead.
    if (settled && dirtyRef.current) {
      await drain();
    }
  }, [drain]);

  const retry = useCallback(async (): Promise<void> => {
    dirtyRef.current = true;
    await flush();
  }, [flush]);

  const ensureFolderStates = useCallback(
    async (folderIds: string[]): Promise<void> => {
      const requested = folderIds.filter(
        folderId =>
          /^\d+$/.test(folderId) && !descendantsRef.current.has(folderId),
      );
      const pending = requested
        .map(folderId => pendingFoldersRef.current.get(folderId))
        .filter((promise): promise is Promise<void> => promise !== undefined);
      const missing = requested.filter(
        folderId => !pendingFoldersRef.current.has(folderId),
      );

      if (missing.length === 0) {
        await Promise.all(pending);
        return;
      }

      let requestPromise: Promise<void>;
      requestPromise = (async () => {
        try {
          const batches = chunkIds(missing, DESCENDANTS_BATCH_SIZE);
          const responses = await Promise.all(
            batches.map(batch => getFolderDescendantsBatch(batch)),
          );

          let resolvedAny = false;
          responses.forEach(response => {
            const resolved = response?.data?.descendants;
            if (!response?.success || !resolved) {
              return;
            }

            Object.entries(resolved).forEach(([folderId, value]) => {
              descendantsRef.current.set(folderId, {
                folderIds: toIdList(value?.folder_ids),
                fileIds: toIdList(value?.file_ids),
              });
              resolvedAny = true;
            });
          });

          if (resolvedAny && !unmountedRef.current) {
            setDescendantsRevision(revision => revision + 1);
          }
        } finally {
          missing.forEach(folderId => {
            if (pendingFoldersRef.current.get(folderId) === requestPromise) {
              pendingFoldersRef.current.delete(folderId);
            }
          });
        }
      })();

      missing.forEach(folderId =>
        pendingFoldersRef.current.set(folderId, requestPromise),
      );

      await Promise.all([...pending, requestPromise]);
    },
    [],
  );

  const setFileSelected = useCallback(
    (fileId: string, shouldSelect: boolean) => {
      applySelection(
        applyFileSelection(selectedRef.current, fileId, shouldSelect),
      );
      schedulePersist();
    },
    [applySelection, schedulePersist],
  );

  const setFolderSelected = useCallback(
    async (folderId: string, shouldSelect: boolean): Promise<void> => {
      if (!descendantsRef.current.has(folderId)) {
        await ensureFolderStates([folderId]);
      }

      applySelection(
        applyFolderSelection(
          selectedRef.current,
          folderId,
          shouldSelect,
          descendantsRef.current,
        ),
      );
      schedulePersist();
    },
    [applySelection, ensureFolderStates, schedulePersist],
  );

  const setRecordsSelected = useCallback(
    async (records: any[], shouldSelect: boolean): Promise<void> => {
      const fileIds: string[] = [];
      const folderIds: string[] = [];

      records.filter(Boolean).forEach(record => {
        const id = String(record.id ?? '');
        if (id === '') {
          return;
        }

        if (record.isLeaf === true || (record.type && record.type !== '')) {
          fileIds.push(id);
        } else {
          folderIds.push(id);
        }
      });

      if (folderIds.length > 0) {
        await ensureFolderStates(folderIds);
      }

      let next = new Set(selectedRef.current);

      fileIds.forEach(fileId => {
        if (shouldSelect) {
          next.add(fileId);
        } else {
          next.delete(fileId);
        }
      });

      folderIds.forEach(folderId => {
        next = applyFolderSelection(
          next,
          folderId,
          shouldSelect,
          descendantsRef.current,
        );
      });

      applySelection(next);
      schedulePersist();
    },
    [applySelection, ensureFolderStates, schedulePersist],
  );

  const getFolderState = useCallback(
    (folderId: string): FolderSelectionState =>
      getFolderSelectionState(
        folderId,
        selectedFileIds,
        descendantsRef.current,
      ),
    // descendantsRevision keeps derived states in sync with the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFileIds, descendantsRevision],
  );

  const isFolderResolved = useCallback(
    (folderId: string): boolean => descendantsRef.current.has(folderId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [descendantsRevision],
  );

  const isFileSelected = useCallback(
    (fileId: string): boolean => selectedFileIds.has(fileId),
    [selectedFileIds],
  );

  return useMemo(
    () => ({
      selectedFileIds,
      selectedFileCount: selectedFileIds.size,
      status,
      isFileSelected,
      getFolderState,
      isFolderResolved,
      setFileSelected,
      setFolderSelected,
      setRecordsSelected,
      ensureFolderStates,
      flush,
      retry,
    }),
    [
      selectedFileIds,
      status,
      isFileSelected,
      getFolderState,
      isFolderResolved,
      setFileSelected,
      setFolderSelected,
      setRecordsSelected,
      ensureFolderStates,
      flush,
      retry,
    ],
  );
};
